//@ToDo: Split to separate helpers camera-zones and canvas-helpers
import type { CameraZone } from '../types/camera-zones';

const ZONE_POINTS = 10_000;

export const DEFAULT_COORDINATES: CameraZone['coordinates'] = [
	ZONE_POINTS * 0.25, //x1
	ZONE_POINTS * 0.25, //y1
	ZONE_POINTS * 0.25, //x2
	ZONE_POINTS * 0.75, //y2
	ZONE_POINTS * 0.75, //x3
	ZONE_POINTS * 0.75, //y3
	ZONE_POINTS * 0.75, //x4
	ZONE_POINTS * 0.25, //y4
];

// Canvas Constants
export const HANDLE_RADIUS_LARGE = 56;
export const CANVAS_LINE_WIDTH = 5;
export const DASH_PATTERN = [10, 12];

export function normalizeRectPoints(points: { xPer: number; yPer: number }[], pointIndex: number) {
	if (points.length !== 4) return points;

	const newPoints = points; // Mutating refs for Svelte reactivity context usually works if triggered, but safe copy is better if strictly functional
	const movedPoint = newPoints[pointIndex];

	// Rectangle point order (CLOCKWISE): [0] top-left, [1] top-right, [2] bottom-right, [3] bottom-left
	const adjacentPoints = [
		{ yFollow: 1, xFollow: 3 }, // Top-left (0)
		{ yFollow: 0, xFollow: 2 }, // Top-right (1)
		{ yFollow: 3, xFollow: 1 }, // Bottom-right (2)
		{ yFollow: 2, xFollow: 0 }, // Bottom-left (3)
	];

	const { yFollow, xFollow } = adjacentPoints[pointIndex];
	newPoints[yFollow].yPer = movedPoint.yPer;
	newPoints[xFollow].xPer = movedPoint.xPer;

	return newPoints;
}

export function prepareZoneCoordinates(coordinates: CameraZone['coordinates']) {
	const coords = [];
	for (let i = 0; i < coordinates.length; i += 2) {
		const x = coordinates[i];
		const y = coordinates[i + 1];
		coords.push({
			xPer: x / ZONE_POINTS,
			yPer: y / ZONE_POINTS,
		});
	}

	const cx = coords.reduce((sum, p) => sum + p.xPer, 0) / coords.length;
	const cy = coords.reduce((sum, p) => sum + p.yPer, 0) / coords.length;

	//Sort points in clockwise order around center
	let sortedCoords = coords.sort((a, b) => {
		const angleA = Math.atan2(a.yPer - cy, a.xPer - cx);
		const angleB = Math.atan2(b.yPer - cy, b.xPer - cx);
		return angleA - angleB;
	});

	return sortedCoords;
}
export function getRectFromPoints(points) {
	const x1 = Math.min(...points.map((p) => p.xPer));
	const y1 = Math.min(...points.map((p) => p.yPer));
	const x2 = Math.max(...points.map((p) => p.xPer));
	const y2 = Math.max(...points.map((p) => p.yPer));

	return [
		{ xPer: x1, yPer: y1 },
		{ xPer: x2, yPer: y1 },
		{ xPer: x2, yPer: y2 },
		{ xPer: x1, yPer: y2 },
	];
}

export function darkenColor(color: CameraZone['color'], factor: number = 0.3): CameraZone['color'] {
	return [Math.round(color[0] * factor), Math.round(color[1] * factor), Math.round(color[2] * factor)];
}

/**
 * Prepare zone coordinates for Camera API
 * @param points Editing points in the canvas
 * @returns CameraZone coordinates in format expected by Camera API
 */
export function prepCoordinatesForZone(points): CameraZone['coordinates'] {
	return getRectFromPoints(points)
		.map((p) => [Math.round(p.xPer * ZONE_POINTS), Math.round(p.yPer * ZONE_POINTS)])
		.flat(2);
}
